import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CopyButton, OnboardingArtistTable, OnboardingSummary, referralLink } from "../components/OnboardingReport";
import "./admin/AdminActionCenter.css";

// Same colour language as the admin Action Center, scoped to this manager's artists.
const SETUP_STEPS = [["basic_profile", "profile"], ["branding", "bio"], ["media", "photos"], ["packages", "packages"], ["availability", "calendar"]];
const MANAGER_ACTIONS = [
  { id: "kyc_fix", severity: "urgent", label: "Urgent", title: "KYC needs fixing",
    description: "KYC was rejected or changes were requested — help the artist resubmit.",
    match: (a) => ["kyc_rejected", "kyc_changes_required"].includes(a.kyc_status), detail: (a) => a.kyc_label },
  { id: "kyc_missing", severity: "high", label: "High", title: "KYC not submitted",
    description: "The artist can't go live until KYC is submitted.",
    match: (a) => !a.kyc_submitted, detail: (a) => `KYC: ${a.kyc_label}` },
  { id: "questionnaire_low", severity: "normal", label: "Normal", title: "Questionnaire under half done",
    description: "Customers book with more confidence when the rider is complete.",
    match: (a) => a.questionnaire_total > 0 && a.questionnaire_percent < 50, detail: (a) => `${a.questionnaire_answered}/${a.questionnaire_total} answered` },
  { id: "setup_incomplete", severity: "followup", label: "Follow-up", title: "Profile setup incomplete",
    description: "Missing basics customers see first.",
    match: (a) => SETUP_STEPS.some(([id]) => !a.steps?.[id]),
    detail: (a) => `Missing: ${SETUP_STEPS.filter(([id]) => !a.steps?.[id]).map(([, label]) => label).join(", ")}` },
];

function ManagerActions({ artists }) {
  const groups = MANAGER_ACTIONS.map((action) => ({ ...action, artists: artists.filter(action.match) })).filter((g) => g.artists.length);
  if (!artists.length) return null;
  if (!groups.length) return <div className="card card-pad mb-16 text-green fw-600">✓ All your artists are up to date — nothing to chase right now.</div>;
  return (
    <div className="action-center mb-16" data-testid="onboarding-actions">
      <div className="action-grid">
        {groups.map((g) => (
          <section key={g.id} className={`action-card sev-${g.severity}`} data-testid={`onboarding-action-${g.id}`}>
            <div className="action-card-head"><span className={`action-sev sev-${g.severity}`}>{g.label}</span></div>
            <div className="action-card-title"><span className="action-count">{g.artists.length}</span><h3>{g.title}</h3></div>
            <p className="action-desc">{g.description}</p>
            <ul className="action-items">
              {g.artists.slice(0, 5).map((a) => (
                <li key={a.artist_id}>
                  <Link to={`/staff/artists/${a.artist_id}`} className="fw-600">{a.stage_name}</Link>
                  <span className="action-item-sub">{g.detail(a)}</span>
                </li>
              ))}
              {g.artists.length > 5 && <li className="action-item-more">+{g.artists.length - 5} more in the table below</li>}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

// Onboarding manager's own view: their referral code + their artists' progress.
export default function OnboardingManagerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/onboarding-manager/me")
      .then((response) => setData(response.data))
      .catch((err) => setError(formatApiError(err)));
  }, []);

  const code = data?.manager?.referral_code;
  return (
    <div data-testid="onboarding-manager-dashboard">
      <Nav />
      <div className="container" style={{ paddingTop: "var(--space-6)", paddingBottom: "var(--space-12)" }}>
        <h1 className="font-serif" style={{ fontSize: 32, fontWeight: 700, margin: 0 }}>Hi, {user?.first_name || "there"}</h1>
        <p className="text-muted mb-16">Track the artists you've onboarded to BookTalent.</p>

        {error && <div className="card card-pad text-red" role="alert">{error}</div>}
        {!data && !error && <div className="loading"><div className="spinner" /></div>}

        {data && (
          <>
            <div className="ob-code-box mb-16" data-testid="onboarding-referral">
              <div>
                <div className="text-muted fs-11">Your referral code</div>
                <div className="ob-code" data-testid="onboarding-referral-code">{code}</div>
              </div>
              <CopyButton text={code} label="Copy code" />
              <div className="ob-link">{referralLink(code)}</div>
              <CopyButton text={referralLink(code)} label="Copy signup link" testId="onboarding-copy-link" />
            </div>
            <div className="text-muted fs-12 mb-16">
              Share the signup link with artists, or ask them to enter your code when they sign up. They'll appear below as soon as they register.
            </div>
            <h2 className="font-serif" style={{ fontSize: 22, margin: "0 0 10px" }}>What needs your attention</h2>
            <ManagerActions artists={data.artists} />
            <OnboardingSummary summary={data.summary} />
            <div className="card card-pad">
              <div className="card-title mb-12">Your artists ({data.artists.length})</div>
              <OnboardingArtistTable artists={data.artists} emptyText="No artists yet — share your signup link to get started." />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
